/**
 * ELF-CIRCLE sponsors — section `#10c`, "Back the circle".
 *
 * A sponsor arrives with two questions — who is watching, and what does it
 * cost — so the section answers both and puts the current roster in between:
 * reach stats, a looping logo belt, a case study with a partner quote, then
 * the three tiers.
 *
 * Everything here is admin-editable, including the marquee roster. Two fields
 * are parsed lists rather than plain strings:
 *
 *   sponsors.partners      one per line, `Name | Category`
 *   sponsors.tierN.perks   one per line, `-` prefix marks an excluded perk
 */

import React from 'react';
import EditableImage from './EditableImage';
import EditableText from './EditableText';
import { useLandingImages } from './useLandingImages';
import { useConsult } from './elfConsult';

const STATS = [
  { value: '280k', label: 'Season reach', note: 'across four cities' },
  { value: '12', label: 'Events a year', note: 'festivals, shows, screenings' },
  { value: '64%', label: 'Aged 18–34', note: 'core ELF audience' },
  { value: '4.2x', label: 'Median ROI', note: 'reported by 2025 partners' },
];

const PARTNERS_DEFAULT = [
  'Northbeat | Sound',
  'Studio Nine | Space',
  'Verso | Print',
  'Halcyon | Drinks',
  'Kite & Co | Apparel',
  'Meridian | Travel',
  'Foundry | Film gear',
  'Lumen | Lighting',
  'Atlas Bank | Finance',
  'Orbit | Ticketing',
  'Pace | Logistics',
  'Vantage | Media',
].join('\n');

interface TierDefaults {
  name: string;
  availability: string;
  price: string;
  cadence: string;
  featured?: boolean;
  cta: string;
  ctaStyle: 'pillcta mag' | 'pillghost';
  /** `-` prefix marks a perk this tier does not include. */
  perks: string;
}

const TIERS: TierDefaults[] = [
  {
    name: 'Headline',
    availability: '1 of 2 available',
    price: '$25k',
    cadence: 'per season',
    featured: true,
    cta: 'Start a conversation',
    ctaStyle: 'pillcta mag',
    perks: [
      'Naming rights on one festival',
      'Main stage & step-and-repeat branding',
      'Six co-produced social posts',
      'Artist residency co-sign',
      'Hospitality for twelve',
      'First refusal on next season',
    ].join('\n'),
  },
  {
    name: 'Supporting',
    availability: '4 available',
    price: '$8k',
    cadence: 'per season',
    cta: 'Enquire',
    ctaStyle: 'pillghost',
    perks: [
      'Logo on all event artwork',
      'Two co-produced social posts',
      'Newsletter feature',
      'Hospitality for four',
      '-Naming rights',
      '-Residency co-sign',
    ].join('\n'),
  },
  {
    name: 'Community',
    availability: 'Open',
    price: '$2k',
    cadence: 'per season',
    cta: 'Enquire',
    ctaStyle: 'pillghost',
    perks: [
      'Logo on the partner wall',
      'One social feature',
      'Named in event credits',
      'Two guest passes',
      '-Co-produced posts',
      '-Hospitality',
    ].join('\n'),
  },
];

const CASE = {
  kicker: 'Case study · Northbeat, 2025',
  title: 'Sound on every stage',
  body:
    'Naming on Neon Circuit plus the residency that put twelve emerging artists on a stage. Brand recall among attendees rose from 11% to 47% over one season.',
};

const QUOTE = {
  label: 'What partners say',
  text:
    '“We reached more of the right people in one festival than in a year of paid media. The circle does not rent you an audience, it introduces you.”',
  initials: 'NB',
  name: 'Ana Roth',
  role: 'Brand director, Northbeat',
};

/** `Name | Category` per line → the marquee's logo plates. */
function parsePartners(value: string): { name: string; kind: string }[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [name, kind = ''] = line.split('|').map((part) => part.trim());
      return { name, kind };
    });
}

/** One perk per line; a leading `-` means the tier excludes it. */
function parsePerks(value: string): { label: string; included: boolean }[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) =>
      line.startsWith('-')
        ? { label: line.slice(1).trim(), included: false }
        : { label: line, included: true },
    );
}

export const ElfSponsors: React.FC = () => {
  const { text, canEdit } = useLandingImages();
  const consult = useConsult();
  const sponsor = (source: string, title: string) => () =>
    consult?.open({ source, interest: 'Sponsorship', title });
  const partners = parsePartners(text('sponsors.partners', PARTNERS_DEFAULT));

  return (
    <section id="sponsors" className="elf-band elf-sponsors">
      <EditableImage slot="sponsors.backdrop" className="elf-sponsors__bg" alt="" />
      <div className="elf-sponsors__scrim" />
      <div className="sigrule elf-band__rule" />
      <div className="elf-band__inner">
        <div className="elf-section__head" style={{ marginBottom: 34 }}>
          <div style={{ maxWidth: 620 }}>
            <span className="eyebrow an" style={{ animationDelay: '.05s' }}>
              <i />
              <EditableText
                contentKey="sponsors.eyebrow"
                fallback="Sponsors & partners"
                label="Eyebrow"
              />
            </span>
            <EditableText
              as="h2"
              contentKey="sponsors.title"
              fallback="Back the circle"
              label="Section title"
              className="an elf-section__title"
              style={{ animationDelay: '.12s' }}
            />
            <EditableText
              as="p"
              contentKey="sponsors.lede"
              fallback="A sponsor arrives with two questions: who is watching, and what does it cost. Both are answered here, with the current roster as the proof in between."
              label="Section intro"
              multiline
              className="an elf-section__lede"
              style={{ animationDelay: '.18s' }}
            />
          </div>
          <div className="an elf-spon__cta" style={{ animationDelay: '.24s' }}>
            <button className="pillghost" type="button" onClick={sponsor('sponsors.kit', 'Request the media kit')}>
              <EditableText contentKey="sponsors.cta.kit" fallback="Media kit" label="Button label" />
            </button>
            <button className="pillcta mag" type="button" onClick={sponsor('sponsors.partner', 'Partner with us')}>
              <EditableText
                contentKey="sponsors.cta.partner"
                fallback="Partner with us"
                label="Button label"
              />{' '}
              <span>→</span>
            </button>
          </div>
        </div>

        <div className="elf-spon__stats">
          {STATS.map((s, i) => (
            <div className="pstat-card" key={s.label}>
              <EditableText
                as="div"
                contentKey={`sponsors.stat${i + 1}.value`}
                fallback={s.value}
                label="Stat figure"
                className="elf-pstat__n"
              />
              <EditableText
                as="div"
                contentKey={`sponsors.stat${i + 1}.label`}
                fallback={s.label}
                label="Stat label"
                className="elf-pstat__k"
              />
              <EditableText
                as="div"
                contentKey={`sponsors.stat${i + 1}.note`}
                fallback={s.note}
                label="Stat note"
                className="elf-pstat__d"
              />
            </div>
          ))}
        </div>

        {/* One control for the whole roster — a pencil on each of 24 plates
            scrolling past would be unusable. */}
        {canEdit && (
          <div className="elf-spon__editrow">
            <EditableText
              contentKey="sponsors.partners"
              fallback={PARTNERS_DEFAULT}
              label="Partner roster — Name | Category"
              multiline
              underline={false}
            >
              {() => <span className="mono">Edit partner roster ({partners.length})</span>}
            </EditableText>
          </div>
        )}

        {/* Roster doubled so the -50% marquee loops without a seam. */}
        <div className="mwrap">
          <div className="mrow" style={{ ['--sp' as string]: '56s' }}>
            {[...partners, ...partners].map((p, i) => (
              <div className="logo" key={`${p.name}-${i}`}>
                <span>{p.name}</span>
                <small>{p.kind}</small>
              </div>
            ))}
          </div>
        </div>

        <div className="elf-spon__proof">
          <div className="elf-spon__case">
            <EditableImage slot="sponsors.case" alt="" />
            <div className="elf-spon__case-scrim" />
            <div className="elf-spon__case-body">
              <EditableText
                as="div"
                contentKey="sponsors.case.kicker"
                fallback={CASE.kicker}
                label="Case study kicker"
                className="elf-spon__case-kicker"
              />
              <EditableText
                as="div"
                contentKey="sponsors.case.title"
                fallback={CASE.title}
                label="Case study title"
                className="elf-spon__case-title"
              />
              <EditableText
                as="p"
                contentKey="sponsors.case.body"
                fallback={CASE.body}
                label="Case study body"
                multiline
              />
            </div>
          </div>

          <div className="scard elf-spon__quote">
            <EditableText
              as="div"
              contentKey="sponsors.quote.label"
              fallback={QUOTE.label}
              label="Quote heading"
              className="mono"
            />
            <EditableText
              as="p"
              contentKey="sponsors.quote.text"
              fallback={QUOTE.text}
              label="Quote"
              multiline
            />
            <div className="elf-spon__who">
              <EditableText
                as="div"
                contentKey="sponsors.quote.initials"
                fallback={QUOTE.initials}
                label="Initials"
                className="elf-spon__avatar"
              />
              <div>
                <EditableText
                  as="div"
                  contentKey="sponsors.quote.name"
                  fallback={QUOTE.name}
                  label="Name"
                  className="elf-spon__who-name"
                />
                <EditableText
                  as="div"
                  contentKey="sponsors.quote.role"
                  fallback={QUOTE.role}
                  label="Role"
                  className="elf-spon__who-role"
                />
              </div>
            </div>
          </div>
        </div>

        <EditableText
          as="div"
          contentKey="sponsors.tiers.label"
          fallback="Choose a tier"
          label="Tiers heading"
          className="mono"
          style={{ marginBottom: 14 }}
        />
        <div className="elf-spon__tiers">
          {TIERS.map((tier, i) => {
            const n = i + 1;
            const perks = parsePerks(text(`sponsors.tier${n}.perks`, tier.perks));
            return (
              <div className={tier.featured ? 'ptier featured' : 'ptier'} key={tier.name}>
                <div className="elf-ptier__head">
                  <EditableText
                    contentKey={`sponsors.tier${n}.name`}
                    fallback={tier.name}
                    label="Tier name"
                    className="elf-ptier__name"
                  />
                  <EditableText
                    contentKey={`sponsors.tier${n}.availability`}
                    fallback={tier.availability}
                    label="Availability"
                    className="mono"
                    style={tier.featured ? { color: 'var(--acc-panel)' } : undefined}
                  />
                </div>
                <div className="elf-ptier__price">
                  <EditableText
                    contentKey={`sponsors.tier${n}.price`}
                    fallback={tier.price}
                    label="Price"
                  />
                  <EditableText
                    contentKey={`sponsors.tier${n}.cadence`}
                    fallback={tier.cadence}
                    label="Cadence"
                  />
                </div>
                <EditableText
                  as="ul"
                  contentKey={`sponsors.tier${n}.perks`}
                  fallback={tier.perks}
                  label="Perks — prefix a line with - to grey it out"
                  multiline
                >
                  {() =>
                    perks.map((perk) => (
                      <li key={perk.label} className={perk.included ? undefined : 'off'}>
                        <b>{perk.included ? '+' : '—'}</b>
                        <span>{perk.label}</span>
                      </li>
                    ))
                  }
                </EditableText>
                <button
                  className={tier.ctaStyle}
                  type="button"
                  onClick={sponsor(`sponsors.tier${n}`, `${tier.name} sponsorship`)}
                >
                  <EditableText
                    contentKey={`sponsors.tier${n}.cta`}
                    fallback={tier.cta}
                    label="Button label"
                  />{' '}
                  <span>→</span>
                </button>
              </div>
            );
          })}
        </div>

        <div className="elf-spon__foot">
          <EditableText
            as="div"
            contentKey="sponsors.foot.note"
            fallback="Bespoke packages available — tell us the season you want to own."
            label="Footer note"
            multiline
          />
          <input className="fld" placeholder="Work email" aria-label="Work email" />
          <button className="pillcta mag" type="button" onClick={sponsor('sponsors.deck', 'Request the deck')}>
            <EditableText
              contentKey="sponsors.foot.cta"
              fallback="Request the deck"
              label="Button label"
            />{' '}
            <span>→</span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default ElfSponsors;
